import PropTypes from 'prop-types';
import React, {createContext, useCallback, useContext, useState} from 'react';
import {hasRoleLevel, roleLevels} from '@account/utils';
import {useAuth} from '@apps-common/auth-react';
import {useFlags} from '@hooks/useFlags';

const UI_MODE_KEY = 'account-ui-mode';

export const UIMode = {
  NEW: 'new',
  CLASSIC: 'classic',
};

// Only 'new'/'classic' count as an explicit, user-made choice; anything else
// (absent, garbage) means the user hasn't chosen and we fall back to the
// rollout default.
const readStoredMode = () => {
  const stored = localStorage.getItem(UI_MODE_KEY);
  return stored === UIMode.NEW || stored === UIMode.CLASSIC ? stored : null;
};

const UIModeContext = createContext({
  mode: UIMode.CLASSIC,
  toggle: () => {},
  isNewUI: false,
});

export const UIModeProvider = ({children}) => {
  const auth = useAuth();
  const {newUiToggle} = useFlags();
  // The New UI is only ever available to planet admins - the same set that can
  // toggle it and the rollout audience. Gate on role, which is resolved before
  // this provider mounts (AuthProvider is `private`), NOT on the LD flag, whose
  // value lags behind identify() on a cold load - gating on the flag would
  // bounce eligible admins to Classic during that window (see the AppBar
  // cold-deep-link fix).
  const isPlanetAdmin = hasRoleLevel(auth, [roleLevels.planet_admin]);
  // The rollout audience: planet admins with the flag on. Deep links carry no
  // UI-mode signal, so without a flag-driven default they always land on Classic.
  const inNewUiRollout = isPlanetAdmin && newUiToggle;

  // The user's explicit choice (via toggle), or null if they haven't chosen.
  const [explicitMode, setExplicitMode] = useState(readStoredMode);

  // Derive the effective mode on every render. Non-admins are always Classic: a
  // stored 'new' preference (shared browser, or an account demoted since it last
  // toggled) must never surface the New UI to anyone outside the audience. For
  // admins, an explicit choice wins; otherwise the rollout audience defaults to
  // New. Deriving during render (rather than syncing state in an effect) keeps
  // isNewUI correct in the same render that auth resolves - otherwise the root
  // redirect reads a stale Classic value for one render and bounces rollout
  // admins to the old UI (and all Organizations) on refresh.
  const mode = isPlanetAdmin
    ? (explicitMode ?? (inNewUiRollout ? UIMode.NEW : UIMode.CLASSIC))
    : UIMode.CLASSIC;

  const toggle = useCallback(() => {
    setExplicitMode(prev => {
      const current = prev ?? (inNewUiRollout ? UIMode.NEW : UIMode.CLASSIC);
      const next = current === UIMode.NEW ? UIMode.CLASSIC : UIMode.NEW;
      localStorage.setItem(UI_MODE_KEY, next);
      return next;
    });
  }, [inNewUiRollout]);

  return (
    <UIModeContext.Provider
      value={{mode, toggle, isNewUI: mode === UIMode.NEW}}
    >
      {children}
    </UIModeContext.Provider>
  );
};

UIModeProvider.propTypes = {
  children: PropTypes.node,
};

export const useUIMode = () => useContext(UIModeContext);
